import { useEffect, useState } from "react";
import { ThumbsUp, ThumbsDown, X, MessageSquare } from "lucide-react";
import {
    submitReportFeedback,
    fetchReviewEligibility,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import ReviewModal from "@/components/feedback/ReviewModal";

const REASONS = [
    { id: "exposure_too_low", label: "Repair exposure too low" },
    { id: "exposure_too_high", label: "Repair exposure too high" },
    { id: "issue_misunderstood", label: "An issue was misunderstood" },
    { id: "missing_issue", label: "An important issue was missing" },
    { id: "too_vague", label: "Too vague" },
    { id: "too_much_detail", label: "Too much detail" },
    { id: "needs_more_negotiation", label: "Not enough negotiation guidance" },
    { id: "other", label: "Other" },
];

/**
 * MicroFeedbackBar — calm "Was this analysis helpful?" widget at the bottom
 * of a report. Pure DB → never invokes AI. Stores in session to avoid
 * nagging the same user repeatedly.
 *
 * Behavior:
 *   - Anonymous / sample viewers: no UI.
 *   - Owners only: shows a thin pair of thumbs.
 *   - "Helpful" → thank-you state + optional "Leave a full review" CTA when
 *     the user is eligible (verified purchase).
 *   - "Needs improvement" → shows a one-line reason chooser and optional comment.
 */
const SESSION_KEY = "bl_microfeedback_v1";

export default function MicroFeedbackBar({ reportId, isSample, ownedTier }) {
    const { user } = useAuth();
    const [hidden, setHidden] = useState(true);
    const [step, setStep] = useState("ask"); // ask | thanks | reason | done
    const [reason, setReason] = useState(null);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [eligibility, setEligibility] = useState(null);
    const [reviewOpen, setReviewOpen] = useState(false);

    useEffect(() => {
        if (!user || isSample || !reportId) return undefined;
        try {
            const dismissed = sessionStorage.getItem(`${SESSION_KEY}:${reportId}`);
            if (dismissed) return undefined;
        } catch {
            /* ignore */
        }
        setHidden(false);
        track("review_prompt_viewed", { report_id: reportId });
        // Check eligibility in the background.
        (async () => {
            try {
                const r = await fetchReviewEligibility(reportId);
                setEligibility(r);
            } catch {
                setEligibility(null);
            }
        })();
    }, [reportId, user, isSample]);

    const dismiss = () => {
        try {
            sessionStorage.setItem(`${SESSION_KEY}:${reportId}`, "1");
        } catch {
            /* ignore */
        }
        setHidden(true);
    };

    const submit = async (helpful) => {
        setSubmitting(true);
        try {
            await submitReportFeedback({
                report_id: reportId,
                helpful,
            });
            track("report_feedback_submitted", {
                report_id: reportId,
                helpful,
            });
            // Defensive — if the user clicks before the initial eligibility
            // fetch settled, fetch it now so the "Leave a verified review"
            // CTA can render synchronously with the thanks state.
            if (helpful && eligibility === null) {
                try {
                    const r = await fetchReviewEligibility(reportId);
                    setEligibility(r);
                } catch {
                    setEligibility({ eligible: false });
                }
            }
            setStep(helpful ? "thanks" : "reason");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save feedback.");
        } finally {
            setSubmitting(false);
        }
    };

    const submitReason = async () => {
        if (!reason) {
            toast.error("Pick what felt off.");
            return;
        }
        setSubmitting(true);
        try {
            await submitReportFeedback({
                report_id: reportId,
                helpful: false,
                feedback_type: reason,
                comment: comment.trim() || null,
            });
            track("report_feedback_submitted", {
                report_id: reportId,
                helpful: false,
                feedback_type: reason,
            });
            setStep("done");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save feedback.");
        } finally {
            setSubmitting(false);
        }
    };

    if (hidden || !user || isSample) return null;
    const eligibleForReview = eligibility?.eligible === true;

    return (
        <>
            <div
                className="bl-card p-5 md:p-6 mt-6 relative"
                data-testid="micro-feedback-bar"
                data-toc-section="feedback"
            >
                <button
                    onClick={dismiss}
                    className="absolute top-3 right-3 text-bl-muted p-1 rounded hover:bg-bl-bg hover:text-bl"
                    aria-label="Dismiss feedback prompt"
                    data-testid="micro-feedback-dismiss"
                >
                    <X className="w-4 h-4" />
                </button>

                {step === "ask" && (
                    <div className="flex items-start gap-4 flex-wrap">
                        <div className="w-9 h-9 rounded-lg bg-bl-teal-tint flex items-center justify-center shrink-0">
                            <MessageSquare className="w-4 h-4 text-bl-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                                Quick feedback
                            </div>
                            <h3 className="mt-0.5 font-display font-semibold text-bl text-lg leading-tight">
                                Was this analysis helpful?
                            </h3>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <button
                                onClick={() => submit(true)}
                                disabled={submitting}
                                className="bl-btn-secondary text-sm"
                                data-testid="micro-feedback-helpful"
                            >
                                <ThumbsUp className="w-4 h-4" /> Helpful
                            </button>
                            <button
                                onClick={() => submit(false)}
                                disabled={submitting}
                                className="bl-btn-secondary text-sm"
                                data-testid="micro-feedback-needs-improvement"
                            >
                                <ThumbsDown className="w-4 h-4" /> Needs work
                            </button>
                        </div>
                    </div>
                )}

                {step === "thanks" && (
                    <div data-testid="micro-feedback-thanks">
                        <h3 className="font-display font-semibold text-bl text-lg">
                            Thanks — your feedback was received.
                        </h3>
                        <p className="mt-1.5 text-sm text-bl-muted leading-relaxed">
                            {eligibleForReview ? (
                                <>
                                    Want to leave a short verified review?
                                    It's optional, helps shape the product,
                                    and stays private to our team unless you
                                    explicitly allow it.
                                </>
                            ) : (
                                <>
                                    Anything else we should know? Use the
                                    "Share feedback" button at the bottom of
                                    the page any time.
                                </>
                            )}
                        </p>
                        {eligibleForReview && (
                            <button
                                onClick={() => setReviewOpen(true)}
                                className="bl-btn-primary text-sm mt-4"
                                data-testid="micro-feedback-open-review"
                            >
                                Leave a verified review
                            </button>
                        )}
                    </div>
                )}

                {step === "reason" && (
                    <div data-testid="micro-feedback-reason">
                        <h3 className="font-display font-semibold text-bl text-lg">
                            What felt off?
                        </h3>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {REASONS.map((r) => (
                                <button
                                    key={r.id}
                                    onClick={() => setReason(r.id)}
                                    className={
                                        "px-3 py-1.5 rounded-full text-xs " +
                                        (reason === r.id
                                            ? "bg-bl-primary text-bl-surface"
                                            : "bg-bl-teal-tint/40 text-bl-primary hover:bg-bl-teal-tint")
                                    }
                                    data-testid={`micro-feedback-reason-${r.id}`}
                                >
                                    {r.label}
                                </button>
                            ))}
                        </div>
                        <textarea
                            placeholder="Anything else we should know? (optional)"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={2}
                            className="mt-3 w-full px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm resize-y"
                            maxLength={2000}
                            data-testid="micro-feedback-comment"
                        />
                        <div className="mt-3 flex gap-2">
                            <button
                                onClick={submitReason}
                                disabled={submitting || !reason}
                                className="bl-btn-primary text-sm"
                                data-testid="micro-feedback-reason-submit"
                            >
                                Send feedback
                            </button>
                            <button
                                onClick={dismiss}
                                className="bl-btn-secondary text-sm"
                            >
                                Skip
                            </button>
                        </div>
                    </div>
                )}

                {step === "done" && (
                    <div data-testid="micro-feedback-done">
                        <h3 className="font-display font-semibold text-bl text-lg">
                            Thanks — your feedback was received.
                        </h3>
                        <p className="mt-1.5 text-sm text-bl-muted leading-relaxed">
                            Our team reviews every note. Use "Share feedback"
                            at the bottom of the page any time.
                        </p>
                    </div>
                )}
            </div>
            {reviewOpen && (
                <ReviewModal
                    open={reviewOpen}
                    onClose={() => setReviewOpen(false)}
                    reportId={reportId}
                    reportTier={ownedTier}
                    eligibility={eligibility}
                />
            )}
        </>
    );
}
